import { Component } from '@verndale/core';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { BodyScrollOptions } from 'body-scroll-lock';
import { rovingIndex } from 'roving-ux';
import { debounce } from '../helpers/debounce';
import { isTabletLandscape } from '../helpers/breakpoints';
import { FocusTrap, createFocusTrap } from 'focus-trap';
import Cookies from 'cookies-ts';
import { COOKIE_NAMES } from '../helpers/global-storage';
import { ExtendedWindowType } from '../helpers/global-types';
import { handleChangePersona } from '../helpers/personalize';

export const HEADER_EVENTS = Object.freeze({
  HEADER_RESIZE: 'header:resize'
});

class Header extends Component {
  private dropdowns: Array<HTMLElement>;
  private dropdownParents: Array<HTMLElement>;
  private focusTrap: FocusTrap | null;
  private originalWidth: number;
  private animateHeader: NodeJS.Timer | null = null;
  private personaMapping: { [key: string]: string };
  private scrollPosition: number;

  constructor(el: HTMLElement) {
    super(el);
    this.dropdownParents = [];
    this.dropdowns = [];
    this.focusTrap = this.createFocusTrap();
    this.originalWidth = window.innerWidth;
    this.personaMapping = (
      document.querySelector('[data-persona-mapping]') as HTMLElement
    )?.dataset?.personaMapping
      ? JSON.parse(
          (document.querySelector('[data-persona-mapping]') as HTMLElement)
            ?.dataset.personaMapping as string
        )
      : null;
    this.scrollPosition = window.scrollY;

    this.updatePersona();
  }

  setupDefaults() {
    this.dom = {
      primaryNav: this.el.querySelector<HTMLElement>('.header__primary-nav'),
      primaryButtons: this.el.querySelectorAll('.header__primary-btn'),
      submenus: this.el.querySelectorAll('.header__primary-submenu'),
      backButton: this.el.querySelectorAll('.header__primary-back-btn'),
      hamburger: this.el.querySelector<HTMLElement>(
        '.header__mobile-hamburger'
      ),
      utilityNav: this.el.querySelector<HTMLElement>('.header__utility-nav'),
      utilityMobile: this.el.querySelectorAll('.header__utility-menu--mobile'),
      utilityLinks: this.el.querySelectorAll('.header__utility-persona-links'),

      utilityData: this.el.querySelector<HTMLElement>(
        '.header__utility-persona-data'
      ),
      utilityDefault: this.el.querySelectorAll<HTMLElement>(
        '.header__utility-default'
      ),
      headerCta: this.el.querySelector<HTMLElement>('#persona-header-cta'),
      headerPersonaPicker: this.el.querySelector<HTMLElement>(
        '.header__utility-persona'
      ),
      headerPersonaPickerList: this.el.querySelector<HTMLElement>(
        '.header__utility-persona .header__utility-dropdown-list'
      ),
      headerPersonaSelect: this.el.querySelectorAll<HTMLElement>(
        '.header__utility-audience-text'
      ),
      headerPersonaPickerOptions:
        this.el.querySelectorAll<HTMLElement>('[data-persona]'),
      headerSearch: this.el.querySelectorAll('.header__search')
    };

    document.documentElement.style.setProperty(
      '--header-block-size',
      `${this.el.offsetHeight}px`
    );

    document.documentElement.style.setProperty(
      '--header-top-offset',
      `${this.el.getBoundingClientRect().top}px`
    );

    this.setRightOffsetForPersonaDropdown();
  }

  addListeners() {
    window.addEventListener('click', this.handleClickOutside.bind(this));
    window.addEventListener('keydown', this.handleEsc.bind(this));
    window.addEventListener(
      'resize',
      debounce(this.handleResize.bind(this), 250)
    );

    (this.dom.utilityNav as HTMLElement)?.addEventListener(
      'animationstart',
      e => {
        this.handleAnimateHeader(e);
      }
    );
    (this.dom.utilityNav as HTMLElement)?.addEventListener(
      'animationend',
      e => {
        if (e.animationName == 'hideUtils') {
          if (this.animateHeader) {
            clearInterval(this.animateHeader);
          }
        }
      }
    );

    if (!this.el.classList.contains('campaign-header'))
      window.addEventListener(
        'scroll',
        debounce(this.handleScroll.bind(this), 100)
      );

    (this.dom.primaryButtons as NodeList).forEach(trigger =>
      trigger.addEventListener('click', this.handlePrimaryClick.bind(this))
    );

    (this.dom.backButton as NodeList).forEach(trigger =>
      trigger.addEventListener('click', this.handleSubmenuBackButton.bind(this))
    );

    (this.dom.hamburger as HTMLElement)?.addEventListener(
      'click',
      this.handleHamburgerClick.bind(this)
    );

    (this.dom.headerPersonaPickerOptions as NodeList).forEach(btn => {
      btn.addEventListener('click', this.handlePersonaPickerClick.bind(this));
    });
  }

  createFocusTrap() {
    const options = {
      reserveScrollBarGap: true,
      allowTouchMove: (el: HTMLElement) => {
        return el.closest('.header') !== null;
      }
    } as BodyScrollOptions;

    const trap = createFocusTrap(this.el, {
      clickOutsideDeactivates: false,
      returnFocusOnDeactivate: true,
      onActivate: () => {
        this.dropdownParents?.forEach(dropdown => {
          const list: HTMLElement = (dropdown as HTMLElement).querySelector(
            '.header__utility-dropdown-list'
          ) as HTMLElement;

          if (list) list.style.opacity = '0';
        });
        this.el.classList.add('open');
        (this.dom.hamburger as HTMLElement).setAttribute(
          'aria-expanded',
          'true'
        );

        disableBodyScroll(this.el, options);
      },
      onDeactivate: () => {
        this.el.classList.remove('open');

        (this.dom.hamburger as HTMLElement).setAttribute(
          'aria-expanded',
          'false'
        );

        clearAllBodyScrollLocks();
        this.closeSubmenu();
      }
    });

    return trap;
  }

  setRightOffsetForPersonaDropdown() {
    const personaDropdownRect = (
      this.dom.headerPersonaPickerList as HTMLElement
    )?.getBoundingClientRect();

    if (personaDropdownRect) {
      const translate = window.innerWidth - personaDropdownRect.right;
      if (translate < 0) {
        (this.dom.headerPersonaPickerList as HTMLElement)?.setAttribute(
          'style',
          `transform: translateX(calc(${translate}px - var(--container-padding)))`
        );
      } else {
        (this.dom.headerPersonaPickerList as HTMLElement)?.setAttribute(
          'style',
          `transform: translateX(0)`
        );
      }
    }
  }

  updatePersona() {
    const cookies = new Cookies();
    let personaCookie = cookies.get(COOKIE_NAMES.persona);
    const persona = document.querySelector('#persona') as HTMLDivElement;

    if (persona?.dataset?.personalize && !personaCookie) {
      personaCookie = persona?.dataset?.personalize;
    }

    if (personaCookie) {
      this.updatePersonaPicker(personaCookie);
      this.updatePersonaButton(personaCookie);
      this.updatePersonaNavigation(personaCookie);
    } else {
      this.updatePersonaButton('');
      this.updatePersonaNavigation('');
    }

    this.dropdownParents = Array.from(
      this.el.querySelectorAll('.header__utility-dropdown')
    );

    this.dropdowns = Array.from(
      this.el.querySelectorAll('.header__utility-dropdown-btn')
    );
    this.dropdowns.forEach(trigger =>
      trigger.addEventListener('click', this.handleDropdownClick.bind(this))
    );

    this.addRovingIndex();
  }

  addRovingIndex() {
    if (
      this.dom.primaryNav &&
      (this.dom.primaryNav as HTMLElement).querySelector('.header__primary-btn')
    ) {
      rovingIndex({
        element: this.dom.primaryNav,
        target: '.header__primary-btn'
      });
    }

    this.dropdownParents?.forEach(dropdown => {
      const list: HTMLElement = (dropdown as HTMLElement).querySelector(
        '.header__utility-dropdown-list'
      ) as HTMLElement;

      if (list) {
        rovingIndex({
          element: list,
          target: '.header__utility-dropdown-link'
        });
      }
    });
  }

  updatePersonaPicker(persona: string) {
    const personaLinks = this.dom
      .headerPersonaSelect as NodeListOf<HTMLElement>;
    if (personaLinks?.length > 0) {
      const selectOptionsArray = Array.from(
        this.dom.headerPersonaPickerOptions as NodeListOf<HTMLButtonElement>
      );
      const button = selectOptionsArray.find(
        el => el.dataset.persona === persona
      );
      const label = button?.innerHTML.trim();
      personaLinks.forEach(persona => (persona.innerText = label ? label : ''));
    }
  }

  async updatePersonaButton(persona: string) {
    if (!this.personaMapping || !this.dom.headerCta) return;
    const personalizationData = {
      channel: 'WEB',
      currency: 'USD',
      pointOfSale: (window as ExtendedWindowType).Engage?.settings?.pointOfSale,
      friendlyId: this.personaMapping?.personaCta,
      // optional attributes:
      params: {
        person_type: persona
      }
    };

    const response = await (window as ExtendedWindowType).engage?.personalize(
      personalizationData
    );

    if (response?.cta?.url) {
      const headerCTA = this.dom.headerCta as HTMLElement;
      headerCTA.setAttribute('href', response.cta.url);
      headerCTA.setAttribute('target', response.cta.target);
      headerCTA.innerText = response.cta.label;
    }
  }

  async updatePersonaNavigation(persona: string) {
    if (!this.personaMapping) return;
    const refPosition = (this.dom.utilityLinks as NodeList)[0];
    const refPositionMobile = (this.dom.utilityLinks as NodeList)[2];

    if (persona === '') {
      (this.dom.utilityDefault as NodeList)?.forEach(element => {
        (element as HTMLElement).removeAttribute('hidden');
      });
      (refPosition as HTMLElement)?.setAttribute('hidden', 'true');
      (refPositionMobile as HTMLElement)?.setAttribute('hidden', 'true');
      return;
    }

    const personalizationData = {
      channel: 'WEB',
      currency: 'USD',
      pointOfSale: (window as ExtendedWindowType).Engage?.settings?.pointOfSale,
      friendlyId: this.personaMapping?.navigationLinks,
      // optional attributes:
      params: {
        person_type: persona
      }
    };

    const response = await (window as ExtendedWindowType).engage?.personalize(
      personalizationData
    );

    const responseNavigationLinks = response?.navigationLinks;

    if (responseNavigationLinks) {
      const previousAdded = document.querySelectorAll('[data-added]');
      previousAdded.forEach(element => {
        (element.closest('li') as HTMLElement)?.remove();
      });

      (this.dom.utilityDefault as NodeList)?.forEach(element => {
        (element as HTMLElement).setAttribute('hidden', 'true');
      });

      responseNavigationLinks.forEach((link, index) => {
        const li = document.createElement('li');
        if (
          link?.navigationLinks?.length &&
          link?.navigationLinks?.length > 0
        ) {
          li.classList.add('header__utility-dropdown');
          const button = document.createElement('button');
          button.classList.add('header__utility-dropdown-btn');
          button.setAttribute('aria-expanded', 'false');
          button.setAttribute(
            'aria-controls',
            `dropdown-${index}-${link.label}`
          );
          button.setAttribute('data-added', 'true');
          button.addEventListener('click', this.handleDropdownClick.bind(this));
          button.innerText = link.label;
          const ul = document.createElement('ul');
          ul.classList.add('header__utility-dropdown-list');
          ul.setAttribute('id', `dropdown-${index}-${link.label}`);
          link.navigationLinks?.forEach(subLinkObj => {
            const subList = document.createElement('li');
            const subLink = document.createElement('a');
            subLink.classList.add('header__utility-dropdown-link');
            subLink.setAttribute('href', subLinkObj.url);
            subLink.setAttribute('target', subLinkObj.target);
            subLink.innerText = subLinkObj.label;
            subList.appendChild(subLink);
            ul.appendChild(subList);
          });
          this.dropdownParents.push(li);
          li.appendChild(button);
          li.appendChild(ul);
        } else {
          const a = document.createElement('a');
          a.setAttribute('href', link.url);
          a.setAttribute('target', link.target);
          a.setAttribute('data-added', 'true');
          a.classList.add('header__utility-link');
          a.innerText = link.label;
          li.appendChild(a);
        }
        const liMobile = li.cloneNode(true);
        refPosition?.parentNode?.insertBefore(li, refPosition);
        refPositionMobile?.parentNode?.insertBefore(
          liMobile,
          refPositionMobile
        );
      });
    }

    (refPosition as HTMLElement)?.setAttribute('hidden', 'true');
    (refPositionMobile as HTMLElement)?.setAttribute('hidden', 'true');
  }

  closeDropdowns() {
    this.dropdownParents.forEach((dropdown, index) => {
      (dropdown as HTMLElement).classList.remove(
        'header__utility-dropdown--open'
      );
      (this.dropdowns[index] as HTMLElement)?.setAttribute(
        'aria-expanded',
        'false'
      );
    });
  }

  handleDropdownClick(e: Event) {
    const target = (e.target as HTMLElement).closest('button') as HTMLElement;
    const parent = target.parentElement;

    if (parent && target) {
      const parentList = target.parentNode;
      const parentDimensions = parent.getBoundingClientRect();
      const dropdownList = parentList?.querySelector(
        '.header__utility-dropdown-list'
      ) as HTMLElement;
      dropdownList.style.opacity = '1';
      if (isTabletLandscape) {
        if (
          dropdownList?.offsetHeight >=
          window.innerHeight - parentDimensions.top - parentDimensions.height
        ) {
          dropdownList.classList.add('header__utility-dropdown-list--top');
        } else {
          dropdownList.classList.remove('header__utility-dropdown-list--top');
        }
      } else {
        if (
          dropdownList.classList.contains('header__utility-dropdown-list--top')
        ) {
          dropdownList.classList.remove('header__utility-dropdown-list--top');
        }
      }
    }
    if (!parent?.classList.contains('header__utility-dropdown--open')) {
      this.closeDropdowns();
      parent?.classList.add('header__utility-dropdown--open');
      target.setAttribute('aria-expanded', 'true');
      const firstLink = parent?.querySelector(
        '.header__utility-dropdown-link'
      ) as HTMLElement;
      firstLink.focus();
    } else {
      parent.classList.remove('header__utility-dropdown--open');
      target.setAttribute('aria-expanded', 'false');
    }
  }

  closeSubmenu() {
    (this.dom.submenus as NodeList).forEach(submenu => {
      (submenu as HTMLElement).classList.remove(
        'header__primary-submenu--open'
      );
    });
    (this.dom.primaryButtons as NodeList).forEach(submenu =>
      (submenu as HTMLElement).setAttribute('aria-expanded', 'false')
    );

    if (isTabletLandscape) {
      (this.dom.primaryButtons as NodeList).forEach(button =>
        (button as HTMLElement).setAttribute('style', '')
      );
      (this.dom.utilityMobile as NodeList).forEach(mobile =>
        (mobile as HTMLElement).setAttribute('style', '')
      );
      (this.dom.headerSearch as NodeList).forEach(search =>
        (search as HTMLElement).setAttribute('style', '')
      );
    }
  }

  handlePrimaryClick(e: Event) {
    const target = (e.currentTarget as HTMLElement).closest(
      'button'
    ) as HTMLElement;
    const submenu = target.nextElementSibling as HTMLElement;

    target.focus();

    if (!submenu?.classList.contains('header__primary-submenu--open')) {
      this.closeSubmenu();
      submenu.style.opacity = '1';
      submenu?.classList.add('header__primary-submenu--open');
      if (isTabletLandscape) {
        setTimeout(() => {
          (this.dom.primaryButtons as NodeList).forEach(button =>
            (button as HTMLElement).setAttribute('style', 'visibility: hidden;')
          );
          (this.dom.utilityMobile as NodeList).forEach(mobile =>
            (mobile as HTMLElement).setAttribute('style', 'visibility: hidden')
          );
          (this.dom.headerSearch as NodeList).forEach(search =>
            (search as HTMLElement).setAttribute('style', 'visibility: hidden')
          );
        }, 400);
      }
      target?.setAttribute('aria-expanded', 'true');
    } else {
      submenu?.classList.remove('header__primary-submenu--open');
      target?.setAttribute('aria-expanded', 'false');
    }
  }

  handleSubmenuBackButton(e: Event) {
    const target = e.currentTarget as HTMLElement;
    const submenu = target.closest('.header__primary-submenu');

    submenu?.classList.remove('header__primary-submenu--open');

    if (isTabletLandscape) {
      (this.dom.primaryButtons as NodeList).forEach(button =>
        (button as HTMLElement).setAttribute('style', '')
      );
      (this.dom.utilityMobile as NodeList).forEach(mobile =>
        (mobile as HTMLElement).setAttribute('style', '')
      );
      (this.dom.headerSearch as NodeList).forEach(search =>
        (search as HTMLElement).setAttribute('style', '')
      );
    }
  }

  handleHamburgerClick() {
    if (!this.el.classList.contains('open')) {
      this.focusTrap?.activate();
    } else {
      this.focusTrap?.deactivate();
    }
  }

  handleEsc(e: KeyboardEvent) {
    if (e.code === 'Escape') {
      this.closeDropdowns();
      this.closeSubmenu();
    }
  }

  handleClickOutside(e: Event) {
    if (!(e.target as HTMLElement)?.closest('.header__utility-dropdown')) {
      this.closeDropdowns();
    }

    if (!(e.target as HTMLElement)?.closest('.header__primary-nav')) {
      if (!isTabletLandscape) {
        this.closeSubmenu();
      }
    }

    if ((e.target as HTMLElement)?.classList.contains('header__primary-btn')) {
      (window as ExtendedWindowType).dataLayer.push({
        event_attributes: {},
        event: 'nav_click',
        event_category: 'navigation',
        event_action: 'top nav',
        event_label: (e.target as HTMLElement).innerHTML.toString().trim()
      });
    }

    if (
      (e.target as HTMLElement)?.closest('.header__primary-nav-link-heading')
    ) {
      (window as ExtendedWindowType).dataLayer.push({
        event_attributes: {
          primary_menu_item: (e.target as HTMLElement)
            .closest('.header__primary-list')
            ?.querySelector('.header__primary-btn')
            ?.innerHTML.toString()
            .trim(),
          secondary_menu_item: ''
        },
        event: 'nav_click',
        event_category: 'navigation',
        event_action: 'mega menu',
        event_label: (e.target as HTMLElement).innerHTML.toString().trim()
      });
    }
    if (
      (e.target as HTMLElement)?.classList.contains('header__primary-nav-link')
    ) {
      (window as ExtendedWindowType).dataLayer.push({
        event_attributes: {
          primary_menu_item: (e.target as HTMLElement)
            .closest('.header__primary-list')
            ?.querySelector('.header__primary-btn')
            ?.innerHTML.toString()
            .trim(),
          secondary_menu_item: (e.target as HTMLElement)
            .closest('.header__primary-nav-list')
            ?.querySelector('.header__primary-nav-link-heading span')
            ?.innerHTML.toString()
            .trim()
        },
        event: 'nav_click',
        event_category: 'navigation',
        event_action: 'mega menu',
        event_label: (e.target as HTMLElement).innerHTML.toString().trim()
      });
    }
  }

  handlePersonaPickerClick(e: Event) {
    const target = e.currentTarget as HTMLElement;
    const persona = target.dataset.persona;
    if (!persona) return;

    handleChangePersona(persona);
    this.updatePersonaButton(persona);
    this.updatePersonaPicker(persona);
    this.updatePersonaNavigation(persona);
    this.closeDropdowns();
  }

  handleResize() {
    this.setRightOffsetForPersonaDropdown();
    document.documentElement.style.setProperty(
      '--header-block-size',
      `${this.el.offsetHeight}px`
    );
    window.dispatchEvent(new Event(HEADER_EVENTS.HEADER_RESIZE));

    if (window.innerWidth !== this.originalWidth) {
      this.originalWidth = innerWidth;

      (this.dom.submenus as NodeList).forEach(
        submenu => ((submenu as HTMLElement).style.opacity = '0')
      );
      this.dropdownParents?.forEach(dropdown => {
        const list: HTMLElement = (dropdown as HTMLElement).querySelector(
          '.header__utility-dropdown-list'
        ) as HTMLElement;

        list.style.opacity = '0';
      });

      if (!isTabletLandscape && this.el.classList.contains('open')) {
        clearAllBodyScrollLocks();
        this.el.classList.remove('open');

        (this.dom.hamburger as HTMLElement).setAttribute(
          'aria-expanded',
          'false'
        );
      }
    }
  }

  handleScroll() {
    if (!this.el.classList.contains('header--scrolled')) {
      if (window.scrollY > 0) {
        this.el.classList.add('header--scrolled');
      }
    } else {
      if (window.scrollY === 0) {
        this.el.classList.remove('header--scrolled');
        document.documentElement.style.setProperty(
          '--header-block-size',
          `${this.el.offsetHeight}px`
        );

        window.dispatchEvent(new Event(HEADER_EVENTS.HEADER_RESIZE));
      }
    }

    document.documentElement.style.setProperty(
      '--header-top-offset',
      `${this.el.getBoundingClientRect().top}px`
    );

    if (window.scrollY !== this.scrollPosition && !isTabletLandscape) {
      this.closeSubmenu();
    }

    this.scrollPosition = window.scrollY;
  }

  handleAnimateHeader(e: AnimationEvent) {
    if (e.animationName == 'hideUtils') {
      this.animateHeader = setInterval(() => {
        document.documentElement.style.setProperty(
          '--header-block-size',
          `${this.el.offsetHeight}px`
        );
        window.dispatchEvent(new Event(HEADER_EVENTS.HEADER_RESIZE));
      }, 1);
    }
  }
}

export default Header;
